import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '@/lib/sentry';

/**
 * ErrorBoundary
 *
 * Catches React render errors and shows a friendly fallback so the whole
 * app doesn't blank out on a single bad component. Logs to console for now;
 * Day 11 wires Sentry into onCaught for production error tracking.
 *
 * Usage:
 *   <ErrorBoundary><App /></ErrorBoundary>
 */
interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- ErrorBoundary is the documented escape hatch (audit F-10).
    console.error('[ErrorBoundary] Render error:', error, info.componentStack);
    reportError(error, { componentStack: info.componentStack });
  }

  reset = () => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="max-w-md w-full rounded-lg border border-border bg-card p-6 space-y-4 text-center">
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              The app hit an unexpected error. Try reloading — if the problem
              persists, contact{' '}
              <a className="underline" href="mailto:support@pharma-sync.ca">
                support@pharma-sync.ca
              </a>
              .
            </p>
            <details className="text-left text-xs text-muted-foreground rounded bg-muted/40 p-2">
              <summary className="cursor-pointer">Error details</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all">
                {this.state.error.message}
              </pre>
            </details>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                Reload
              </button>
              <button
                type="button"
                onClick={this.reset}
                className="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-muted/40"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
